import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

export const FIELDS = ["load_number", "broker_name", "broker_address", "rate", "pickup_location", "delivery_location", "pickup_date", "delivery_date", "pickup_appt", "delivery_appt"];
const REQUIRED = ["load_number", "broker_name", "pickup_location", "delivery_location"];
export const MAX_FILES = 30;
const CONCURRENCY = 3;
const blankFields = () => Object.fromEntries(FIELDS.map((k) => [k, ""]));
export const isRateconFile = (f) => f && (f.type === "application/pdf" || f.type.startsWith("image/") || /\.pdf$/i.test(f.name));
export const isValid = (f) => REQUIRED.every((k) => String(f[k]).trim()) && parseFloat(f.rate) > 0;
const num = (r) => String(r.fields.load_number).trim();

let seq = 0;

export function useBulkImport(api) {
  const [rows, setRows] = useState([]);
  const [existing, setExisting] = useState({});
  const [creating, setCreating] = useState(false);
  const urls = useRef(new Set());

  const patch = useCallback((key, p) => setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...(typeof p === "function" ? p(r) : p) } : r))), []);

  const extract = useCallback(async (row) => {
    try {
      const fd = new FormData();
      fd.append("file", row.file);
      const { data } = await api.post("/ratecon/extract", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
      const fields = blankFields();
      FIELDS.forEach((k) => { const v = data.fields[k]; if (v !== null && v !== undefined) fields[k] = String(v); });
      patch(row.key, { phase: "ready", fields, broker: data.broker || null, flagged: data.flagged || [], notes: data.notes || "", checked: !(data.flagged || []).length && isValid(fields) });
    } catch (e) {
      patch(row.key, { phase: "ready", failed: true, flagged: [...FIELDS], checked: false, error: e?.response?.data?.detail || "Couldn't read this ratecon. Fill in the details by hand or remove it." });
    }
  }, [api, patch]);

  useEffect(() => {
    const reading = rows.filter((r) => r.phase === "reading").length;
    const next = rows.filter((r) => r.phase === "queued").slice(0, Math.max(0, CONCURRENCY - reading));
    next.forEach((r) => { patch(r.key, { phase: "reading" }); extract(r); });
  }, [rows, patch, extract]);

  const pending = rows.filter((r) => r.phase !== "created");
  const numbersKey = [...new Set(pending.map(num).filter(Boolean))].sort().join("\n");
  useEffect(() => {
    if (!numbersKey) return undefined;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/loads/check-numbers", { numbers: numbersKey.split("\n") });
        setExisting(data.existing);
      } catch { /* checked again on create */ }
    }, 400);
    return () => clearTimeout(t);
  }, [numbersKey, api]);

  useEffect(() => () => { urls.current.forEach((u) => URL.revokeObjectURL(u)); }, []);

  const dupOf = useMemo(() => {
    const seen = new Set();
    const out = {};
    pending.forEach((r) => {
      const n = num(r);
      if (!n || r.phase === "queued" || r.phase === "reading") return;
      if (existing[n]) out[r.key] = existing[n];
      else if (seen.has(n)) out[r.key] = "batch";
      seen.add(n);
    });
    return out;
  }, [pending, existing]);

  const selectable = (r) => r.phase === "ready" && isValid(r.fields) && !dupOf[r.key];
  const selected = rows.filter((r) => r.checked && selectable(r));
  const needsAttention = (r) => r.phase !== "created" && (r.failed || r.flagged.length > 0 || !isValid(r.fields) || !!dupOf[r.key]);
  const reset = () => { urls.current.forEach((u) => URL.revokeObjectURL(u)); urls.current.clear(); setRows([]); setExisting({}); };

  const addFiles = (list) => {
    const files = [...list];
    const ok = files.filter(isRateconFile);
    if (ok.length < files.length) toast.error(`${files.length - ok.length} file(s) skipped: use PDFs or images`);
    const room = MAX_FILES - pending.length;
    if (ok.length > room) toast.error(`Up to ${MAX_FILES} ratecons at a time. ${ok.length - Math.max(room, 0)} file(s) not added.`);
    const add = ok.slice(0, Math.max(room, 0)).map((file) => {
      const url = URL.createObjectURL(file);
      urls.current.add(url);
      return { key: `r${++seq}`, file, url, phase: "queued", fields: blankFields(), flagged: [], notes: "", checked: false };
    });
    if (add.length) setRows((rs) => [...rs, ...add]);
  };

  const setField = (key, k, v) => patch(key, (r) => ({ fields: { ...r.fields, [k]: v }, flagged: r.flagged.filter((x) => x !== k), edited: true }));
  const setChecked = (key, v) => patch(key, { checked: v });
  const remove = (key) => setRows((rs) => rs.filter((r) => {
    if (r.key === key) { URL.revokeObjectURL(r.url); urls.current.delete(r.url); }
    return r.key !== key;
  }));
  const selectAll = (v) => setRows((rs) => rs.map((r) => (selectable(r) ? { ...r, checked: v } : r)));

  const createSelected = async () => {
    const todo = selected;
    setCreating(true);
    let made = 0;
    for (const r of todo) {
      patch(r.key, { phase: "creating", error: "" });
      const f = r.fields;
      try {
        const { broker_address: addr, ...rest } = f;
        const { data: load } = await api.post("/loads", {
          ...rest, rate: parseFloat(f.rate), assigned_driver_id: null, broker_details: { ...(r.broker || {}), address: addr.trim() || undefined }, pickup_date: f.pickup_date || null, delivery_date: f.delivery_date || null, pickup_appt: f.pickup_appt || null, delivery_appt: f.delivery_appt || null,
        });
        let error = "";
        try {
          const fd = new FormData();
          fd.append("type", "Ratecon");
          fd.append("file", r.file);
          await api.post(`/loads/${load.id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        } catch {
          error = "Load created, but the ratecon couldn't be attached. Open the load to upload it.";
        }
        made += 1;
        patch(r.key, { phase: "created", loadId: load.id, checked: false, error });
      } catch (e) {
        if (e?.response?.status === 409) setExisting((x) => ({ ...x, [num(r)]: /trash/i.test(e.response.data?.detail || "") ? "trash" : "live" }));
        patch(r.key, { phase: "ready", checked: false, error: e?.response?.data?.detail || "Couldn't create this load" });
      }
    }
    setCreating(false);
    const failed = todo.length - made;
    if (failed) toast.error(`${failed} not created. See the flagged rows.`);
    return { made, failed };
  };

  return { rows, pending, dupOf, selectable, selected, needsAttention, creating, addFiles, setField, setChecked, remove, reset, selectAll, createSelected };
}
